import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type {
  CreatePartnerReferralInput,
  PropertyType,
  ReferralStage,
} from '@valutten/shared-schemas/dist/partnerNetwork';
import { functions } from '../lib/firebase';
import { referralErrorMessage } from '../utils/partnerReferrals';

/**
 * Partner Network (subscriber side). Four callables in the LOYALTY codebase
 * (same Firebase project and region as this app's functions instance, so they
 * are called by name like any other callable). Contract:
 * docs/plans/2026-09-26-PARTNER-NETWORK-PORTAL.md §7.
 */

export interface ReferralPartner {
  partnerId: string;
  name: string;
  shortName?: string | null;
  tagline?: string | null;
}

/**
 * A broker the caller may refer on behalf of. A broker gets only themselves;
 * owners, administrators and assistants get the brokers in their scope.
 */
export interface ReferringBroker {
  brokerDocId: string;
  name: string;
  isSelf: boolean;
}

/**
 * The tenant entry point always names the broker the referral is made for;
 * the fee on settlement is owed to that broker. The shared schema makes the
 * field optional (outside referrers never send it); this app always sends it.
 */
export type TenantCreatePartnerReferralInput = CreatePartnerReferralInput & {
  referringBrokerDocId: string;
};

/** Referrer view of a referral (never the partner's own fee). */
export interface PartnerReferralView {
  id: string;
  partnerId: string;
  clientName: string;
  clientEmail?: string | null;
  clientMobile?: string | null;
  budgetCents?: number | null;
  propertyType?: PropertyType | null;
  goal?: string | null;
  market?: string | null;
  financeStatus?: string | null;
  notes?: string | null;
  stage: ReferralStage;
  purchasePriceCents?: number | null;
  /** Owed to the referrer on settlement, GST inclusive; null until the price is known. */
  referralFeeIncGstCents: number | null;
  totalPayoutIncGstCents?: number | null;
  /** ISO string or null; timestampToMillis also reads Timestamp JSON. */
  createdAt: unknown;
  stageChangedAt?: unknown;
  /** Who referred (the broker's name and the tenant's firm). */
  referrerDisplay?: { name: string; firm: string | null } | null;
  /** The broker the referral was made for (the fee is owed to them). */
  referringBrokerDocId?: string | null;
  referringBrokerName?: string | null;
}

interface ListPartnersResponse {
  partners: ReferralPartner[];
}

interface ListBrokersResponse {
  brokers: ReferringBroker[];
}

interface ListReferralsResponse {
  referrals: PartnerReferralView[];
  truncated: boolean;
}

interface CreateReferralResponse {
  referralId: string;
}

export interface UsePartnerReferralsResult {
  partners: ReferralPartner[];
  brokers: ReferringBroker[];
  referrals: PartnerReferralView[];
  truncated: boolean;
  loading: boolean;
  refreshing: boolean;
  partnersError: string | null;
  brokersError: string | null;
  referralsError: string | null;
  refresh: () => Promise<void>;
  /** Resolves to the new referral id; rejects with a user-facing Error message. */
  createReferral: (input: TenantCreatePartnerReferralInput) => Promise<string>;
}

export function usePartnerReferrals(): UsePartnerReferralsResult {
  const [partners, setPartners] = useState<ReferralPartner[]>([]);
  const [brokers, setBrokers] = useState<ReferringBroker[]>([]);
  const [referrals, setReferrals] = useState<PartnerReferralView[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [partnersError, setPartnersError] = useState<string | null>(null);
  const [brokersError, setBrokersError] = useState<string | null>(null);
  const [referralsError, setReferralsError] = useState<string | null>(null);

  const loadPartners = useCallback(async () => {
    try {
      const fn = httpsCallable<Record<string, never>, ListPartnersResponse>(functions, 'tenantListReferralPartners');
      const res = await fn({});
      setPartners(Array.isArray(res.data?.partners) ? res.data.partners : []);
      setPartnersError(null);
    } catch (err) {
      setPartnersError(referralErrorMessage(err));
    }
  }, []);

  const loadBrokers = useCallback(async () => {
    try {
      const fn = httpsCallable<Record<string, never>, ListBrokersResponse>(functions, 'tenantListReferringBrokers');
      const res = await fn({});
      setBrokers(Array.isArray(res.data?.brokers) ? res.data.brokers : []);
      setBrokersError(null);
    } catch (err) {
      setBrokersError(referralErrorMessage(err));
    }
  }, []);

  const loadReferrals = useCallback(async () => {
    try {
      const fn = httpsCallable<Record<string, never>, ListReferralsResponse>(functions, 'tenantListPartnerReferrals');
      const res = await fn({});
      setReferrals(Array.isArray(res.data?.referrals) ? res.data.referrals : []);
      setTruncated(res.data?.truncated === true);
      setReferralsError(null);
    } catch (err) {
      setReferralsError(referralErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadPartners(), loadBrokers(), loadReferrals()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadPartners, loadBrokers, loadReferrals]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([loadPartners(), loadBrokers(), loadReferrals()]);
    } finally {
      setRefreshing(false);
    }
  }, [loadPartners, loadBrokers, loadReferrals]);

  const createReferral = useCallback(
    async (input: TenantCreatePartnerReferralInput) => {
      let referralId: string;
      try {
        const fn = httpsCallable<TenantCreatePartnerReferralInput, CreateReferralResponse>(
          functions,
          'tenantCreatePartnerReferral'
        );
        const res = await fn(input);
        referralId = res.data.referralId;
      } catch (err) {
        throw new Error(referralErrorMessage(err));
      }
      await loadReferrals();
      return referralId;
    },
    [loadReferrals]
  );

  return {
    partners,
    brokers,
    referrals,
    truncated,
    loading,
    refreshing,
    partnersError,
    brokersError,
    referralsError,
    refresh,
    createReferral,
  };
}
